import * as React from "react";

const motionPropNames = new Set([
  "initial",
  "animate",
  "exit",
  "transition",
  "viewport",
  "whileInView",
  "whileHover",
  "whileTap",
  "layout",
  "layoutId",
]);

type MotionProps = {
  children?: React.ReactNode;
  [key: string]: unknown;
};

const cache = new Map<string | React.ElementType, React.ComponentType<MotionProps>>();

const createMotionComponent = (tag: string | React.ElementType) => {
  const cached = cache.get(tag);
  if (cached) return cached;

  const Component = React.forwardRef<HTMLElement, MotionProps>(({ children, ...props }, ref) => {
    const domProps = Object.fromEntries(
      Object.entries(props).filter(([key]) => !motionPropNames.has(key))
    );

    return React.createElement(
      tag,
      { ...(domProps as Record<string, unknown>), ref } as React.HTMLAttributes<HTMLElement>,
      children as React.ReactNode
    );
  }) as unknown as React.ComponentType<MotionProps>;

  cache.set(tag, Component);
  return Component;
};

const createCallableMotion = () => {
  const motionFunction = ((component: React.ElementType) =>
    createMotionComponent(component)) as unknown as Record<string, unknown>;

  motionFunction.create = (component: React.ElementType) => createMotionComponent(component);
  motionFunction.custom = (component: React.ElementType) => createMotionComponent(component);

  return new Proxy(motionFunction, {
    apply: (_target, _thisArg, args: [React.ElementType?]) => {
      const [component] = args;
      return component ? createMotionComponent(component) : createMotionComponent("div");
    },
    get: (target, tag: string) =>
      tag in target
        ? target[tag]
        : createMotionComponent(tag),
  });
};

export const motion = createCallableMotion() as unknown as ((
  component: React.ElementType
) => React.ComponentType<MotionProps>) &
  Record<string, React.ComponentType<MotionProps>> & {
  create: (component: React.ElementType) => React.ComponentType<MotionProps>;
  custom: (component: React.ElementType) => React.ComponentType<MotionProps>;
};

export const AnimatePresence = ({ children }: { children?: React.ReactNode; mode?: string }) => <>{children}</>;

export const useInView = () => true;

export const m = motion;

export const domAnimation = {};
export const domMax = {};
export const LazyMotion = ({ children }: { children?: React.ReactNode; features?: unknown; strict?: boolean }) => <>{children}</>;
export const MotionConfig = ({ children }: { children?: React.ReactNode; [key: string]: unknown }) => <>{children}</>;
export const useReducedMotion = () => false;
export const useAnimation = () => ({ start: async () => {}, stop: () => {}, set: () => {} });
export const useMotionValue = (initial: unknown) => ({ get: () => initial, set: () => {}, on: () => () => {} });
export const useTransform = () => ({ get: () => 0, set: () => {}, on: () => () => {} });
export const useScroll = () => ({
  scrollY: { get: () => 0, set: () => {}, on: () => () => {} },
  scrollYProgress: { get: () => 0, set: () => {}, on: () => () => {} },
});
export const AnimateSharedLayout = ({ children }: { children?: React.ReactNode }) => <>{children}</>;
